import { useState } from "react";
import Header from "@/components/Header";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Send, Loader2, ChevronLeft, Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ContactUs() {
  const [f, setF] = useState({ name: "", email: "", subject: "General enquiry", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim() || !f.message.trim()) {
      toast.error("Please fill in your name, email, and message");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", f);
      toast.success("Thanks — we'll be in touch soon.");
      setSent(true);
      setF({ name: "", email: "", subject: "General enquiry", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not send. Please try again shortly.");
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A1A33] text-white" data-testid="contact-page">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 pb-20">
        <Link to="/" className="inline-flex items-center gap-1 text-caption text-slate-400 hover:text-red-300 mb-4" data-testid="contact-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Back home
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-red-500/15 border border-red-400/30 grid place-items-center">
            <Mail className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <div className="text-caption">Get in touch</div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-tight">Contact us</h1>
          </div>
        </div>
        <p className="text-slate-400 mt-2 text-sm">Questions about a ride, a corporate account, or a driver application — we usually reply within one business day.</p>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          <div className="glass-card gold-border rounded-2xl p-6 space-y-4 fade-up">
            <InfoRow icon={Mail} label="Email" value={<a href="mailto:ttriptap@gmail.com" className="text-red-300 hover:text-red-200" data-testid="contact-email">ttriptap@gmail.com</a>} />
            <InfoRow icon={Phone} label="Phone" value={<a href="tel:+64225250123" className="text-red-300 hover:text-red-200" data-testid="contact-phone">+64 22 525 0123</a>} />
            <InfoRow icon={MapPin} label="Service area" value="Auckland, Aotearoa NZ" />
            <InfoRow icon={Building2} label="Business" value={<>Sathi Limited<br /><span className="text-slate-500 font-mono text-xs">NZBN 9429052213656</span></>} />
            <div className="pt-3 border-t border-white/10 text-xs text-slate-500">
              For legal/privacy queries, see our <Link to="/privacy" className="text-red-300 hover:text-red-200 underline underline-offset-2">Privacy Policy</Link> or <Link to="/terms" className="text-red-300 hover:text-red-200 underline underline-offset-2">Terms & Conditions</Link>.
            </div>
          </div>

          <form onSubmit={submit} className="glass-card gold-border rounded-2xl p-6 space-y-4 fade-up fade-up-1" data-testid="contact-form">
            {sent ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-400/30 grid place-items-center mx-auto mb-3">
                  <Send className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="font-display font-bold text-xl">Message sent</h3>
                <p className="text-slate-400 text-sm mt-1">We'll get back to you at the email you provided.</p>
                <Button onClick={() => setSent(false)} variant="outline" className="mt-4 bg-transparent border-white/10 text-slate-300" data-testid="contact-send-another">Send another</Button>
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-xs uppercase text-slate-400">Your name *</Label>
                  <Input required data-testid="contact-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-black/30 border-white/10 h-11 mt-2" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-400">Email *</Label>
                  <Input required type="email" data-testid="contact-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-black/30 border-white/10 h-11 mt-2" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-400">Subject</Label>
                  <select
                    value={f.subject}
                    onChange={(e) => setF({ ...f, subject: e.target.value })}
                    className="w-full bg-black/30 border border-white/10 rounded-md h-11 px-3 mt-2 text-sm"
                    data-testid="contact-subject"
                  >
                    <option>General enquiry</option>
                    <option>Ride issue / refund</option>
                    <option>Corporate account</option>
                    <option>Driver application</option>
                    <option>Feedback</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-400">Message *</Label>
                  <textarea
                    required rows={5}
                    value={f.message}
                    onChange={(e) => setF({ ...f, message: e.target.value })}
                    className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 mt-2 text-sm resize-y"
                    placeholder="Tell us how we can help…"
                    data-testid="contact-message"
                  />
                </div>
                <Button type="submit" disabled={sending} className="w-full btn-gold rounded-lg h-11" data-testid="contact-submit">
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send message
                </Button>
              </>
            )}
          </form>
        </div>
      </main>
    </div>
  );
}

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 rounded-md bg-red-500/10 border border-red-400/20 grid place-items-center flex-shrink-0 mt-0.5">
      <Icon className="w-3.5 h-3.5 text-red-400" />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">{label}</div>
      <div className="text-white font-medium">{value}</div>
    </div>
  </div>
);
